/**
 * useContainerLayout Hook
 *
 * Main React hook for container layout operations.
 * Provides high-level API for adding/removing containers with validation.
 */

import { useMemo, useCallback } from 'react';
import { useUIModelState } from '../../contexts/UIModelStateContext';
import { createContainerAPI } from '../../types/api.types';
import type { ContainerAPI } from '../../types/api.types';
import type {
  ContainerRow,
  SlotAvailability,
  AddContainerOptions,
  AddContainerResult,
  ValidationResult,
  WidthUpdate,
} from '../types/container-row.types';
import { CONTAINER_CONSTRAINTS } from '../constants/constraints';
import {
  selectActiveContainerRows,
  selectActiveNextAvailableSlot,
  selectIsActiveRowFull,
  selectNestedContainerRows,
  selectIsNestedRowFull,
} from '../selectors/rowSelectors';
import {
  validateAddContainer,
  validateRowConstraint,
  validateNestingDepth,
  validationSuccess,
  validationFailure,
  createValidationError,
} from '../validators';
import {
  calculateWidthsOnAdd,
  createWidthUpdates,
  distributeWidthsEqually,
} from '../utils/widthCalculations';
import {
  getNextAvailableSlot,
  getContainersInRow,
  getNextOrderInRow,
} from '../utils/rowCalculations';
import * as baseSelectors from '../../selectors';
import {
  createAddContainerCommand,
  createBatchCommand,
  createUpdateContainerCommand,
} from '../../types/command.types';

// Generate unique ID for new containers
const generateId = (prefix: string) =>
  `${prefix}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

/**
 * Hook return type
 */
export interface UseContainerLayoutReturn {
  // Data
  containerRows: ContainerRow[];

  // Queries
  canAddToRowIndex: (rowIndex: number) => boolean;
  getNextAvailableSlot: () => SlotAvailability | null;
  getNestedContainerRows: (parentId: string) => ContainerRow[];
  canNestIn: (parentId: string, rowIndex?: number) => boolean;
  getNextContainerNumber: () => number;
  canAddToNestedRow: (parentId: string, rowIndex: number) => boolean;
  getNextNestedContainerNumber: (parentId: string) => number;
  getNestedContainerName: (parentId: string) => string;

  // Operations
  addContainerToRow: (rowIndex: number, name: string, options?: AddContainerOptions) => AddContainerResult;
  addContainerAfterRow: (afterRowIndex: number, name: string, options?: AddContainerOptions) => AddContainerResult;
  addNestedContainer: (parentId: string, name: string, options?: AddContainerOptions) => AddContainerResult;
  addNestedContainerAfterRow: (parentId: string, afterRowIndex: number, name: string, options?: AddContainerOptions) => AddContainerResult;

  // Constants
  MAX_CONTAINERS_PER_ROW: number;
  MAX_NESTING_DEPTH: number;
}

/**
 * Main hook for container layout management
 *
 * @param canvasId - Optional canvas ID (uses active canvas if not provided)
 * @returns Container layout operations and data
 */
export const useContainerLayout = (canvasId?: string): UseContainerLayoutReturn => {
  const { state, dispatch } = useUIModelState();

  // Resolve canvas ID
  const resolvedCanvasId = useMemo(() => {
    if (canvasId) return canvasId;
    return baseSelectors.selectActiveCanvasId(state);
  }, [canvasId, state]);

  // Get containers for the canvas
  const containers = useMemo(() => {
    if (!resolvedCanvasId) return [];
    return baseSelectors.selectContainersForCanvas(state, resolvedCanvasId);
  }, [state, resolvedCanvasId]);

  // Get container rows
  const containerRows = useMemo(() => {
    return selectActiveContainerRows(state);
  }, [state]);

  // ============================================================================
  // Query Functions
  // ============================================================================

  /**
   * Checks if a row can accept more containers
   */
  const canAddToRowIndex = useCallback(
    (rowIndex: number): boolean => {
      if (!resolvedCanvasId) return false;
      return !selectIsActiveRowFull(state, rowIndex);
    },
    [state, resolvedCanvasId]
  );

  /**
   * Gets the next available slot
   */
  const getNextSlot = useCallback((): SlotAvailability | null => {
    return selectActiveNextAvailableSlot(state);
  }, [state]);

  /**
   * Gets nested container rows for a parent
   */
  const getNestedContainerRows = useCallback(
    (parentId: string): ContainerRow[] => {
      return selectNestedContainerRows(state, parentId);
    },
    [state]
  );

  /**
   * Checks if nesting is allowed in a container
   */
  const canNestIn = useCallback(
    (parentId: string, rowIndex: number = 0): boolean => {
      // Check nesting depth
      const depthValidation = validateNestingDepth(containers, parentId);
      if (!depthValidation.isValid) return false;

      // Check row capacity within nested containers
      return !selectIsNestedRowFull(state, parentId, rowIndex);
    },
    [state, containers]
  );

  /**
   * Gets the next sequential container number
   * Parses existing container names to find the highest number and returns max + 1
   */
  const getNextContainerNumber = useCallback((): number => {
    const existingNumbers = containers
      .filter(c => !c.metadata?.isNested)
      .map(c => {
        const match = c.name.match(/Container (\d+)$/);
        return match ? parseInt(match[1], 10) : 0;
      });

    const maxNumber = existingNumbers.length > 0 ? Math.max(...existingNumbers) : 0;
    return maxNumber + 1;
  }, [containers]);

  /**
   * Checks if a nested row within a parent can accept more containers
   */
  const canAddToNestedRow = useCallback(
    (parentId: string, rowIndex: number): boolean => {
      return !selectIsNestedRowFull(state, parentId, rowIndex);
    },
    [state]
  );

  /**
   * Gets the next sequential nested container sub-number for a parent
   * Returns the sub-number only (e.g., 1, 2, 3) - caller builds full name
   * Parses existing nested container names with format "Container X.Y" or "Container X.Y.Z"
   */
  const getNextNestedContainerNumber = useCallback(
    (parentId: string): number => {
      const parentContainer = baseSelectors.selectContainerById(state, parentId).element;
      if (!parentContainer) return 1;

      // Extract full path from parent (e.g., "Container 1" → "1", "Container 1.1" → "1.1")
      const parentMatch = parentContainer.name.match(/Container ([\d.]+)$/);
      const parentPath = parentMatch ? parentMatch[1] : null;

      const nestedContainers = parentContainer.content?.nestedContainers || [];
      const existingNumbers = nestedContainers.map(c => {
        // Match "Container X.Y" or "Container X.Y.Z" where X (or X.Y) is parent path
        // Escape dots in parentPath for regex
        const escapedPath = parentPath ? parentPath.replace(/\./g, '\\.') : null;
        const pattern = escapedPath
          ? new RegExp(`Container ${escapedPath}\\.(\\d+)$`)
          : /Container [\d.]+\.(\d+)$/;
        const match = c.name.match(pattern);
        return match ? parseInt(match[1], 10) : 0;
      });

      const maxNumber = existingNumbers.length > 0 ? Math.max(...existingNumbers) : 0;
      return maxNumber + 1;
    },
    [state]
  );

  /**
   * Generates a nested container name based on parent name
   * e.g., Parent "Container 1" → "Container 1.1", "Container 1.2", etc.
   * e.g., Parent "Container 1.1" → "Container 1.1.1", "Container 1.1.2", etc.
   */
  const getNestedContainerName = useCallback(
    (parentId: string): string => {
      const parentContainer = baseSelectors.selectContainerById(state, parentId).element;
      if (!parentContainer) return `Container 1.1`;

      // Extract full path from parent (e.g., "Container 1" → "1", "Container 1.1" → "1.1")
      const parentMatch = parentContainer.name.match(/Container ([\d.]+)$/);
      const parentPath = parentMatch ? parentMatch[1] : '1';

      const subNumber = getNextNestedContainerNumber(parentId);
      return `Container ${parentPath}.${subNumber}`;
    },
    [state, getNextNestedContainerNumber]
  );

  /**
   * Gets the nesting depth of a parent container
   * Used to calculate the nestingLevel for new nested containers
   * @param parentId - ID of the parent container
   * @returns Nesting depth (0 = top-level, 1 = nested under top-level, etc.)
   */
  const getNestingDepthOfParent = useCallback(
    (parentId: string): number => {
      const parentContainer = baseSelectors.selectContainerById(state, parentId).element;
      if (!parentContainer) return 0;
      return parentContainer.metadata?.nestingLevel ?? 0;
    },
    [state]
  );

  // ============================================================================
  // Operation Functions
  // ============================================================================

  /**
   * Adds a container to an existing row
   */
  const addContainerToRow = useCallback(
    (rowIndex: number, name: string, options: AddContainerOptions = {}): AddContainerResult => {
      if (!resolvedCanvasId) {
        return {
          success: false,
          validation: validationFailure([
            createValidationError('CANVAS_NOT_FOUND', 'No active canvas found'),
          ]),
        };
      }

      const {
        width: explicitWidth,
        widthStrategy = 'equal',
        parentContainerId,
        insertAtOrder,
      } = options;

      // Get containers in target row
      const rowContainers = getContainersInRow(containers, rowIndex);

      // Calculate width
      const newContainerId = generateId('container');
      const widthMap = calculateWidthsOnAdd(rowContainers, newContainerId, widthStrategy);
      const calculatedWidth = explicitWidth || widthMap.get(newContainerId) || distributeWidthsEqually(rowContainers.length + 1);

      // Validate
      const validation = validateAddContainer(
        containers,
        rowIndex,
        parentContainerId || null,
        calculatedWidth
      );

      if (!validation.isValid) {
        return { success: false, validation };
      }

      // Determine the order for the new container
      let newOrder: number;
      const orderShiftCommands: ReturnType<typeof createUpdateContainerCommand>[] = [];

      if (insertAtOrder !== undefined) {
        // Clamp insertAtOrder to valid range [0, rowContainers.length]
        newOrder = Math.max(0, Math.min(insertAtOrder, rowContainers.length));

        // Find containers at or after the insert position and shift their order +1
        const containersToShift = rowContainers
          .filter((c) => c.order >= newOrder)
          .sort((a, b) => b.order - a.order); // Sort descending to shift from end first

        for (const container of containersToShift) {
          orderShiftCommands.push(
            createUpdateContainerCommand(
              {
                containerId: container.id,
                canvasId: resolvedCanvasId,
                updates: {
                  order: container.order + 1,
                },
              },
              `Shift order: ${container.order} → ${container.order + 1}`
            )
          );
        }
      } else {
        // Use existing behavior: get next available order
        newOrder = getNextOrderInRow(containers, rowIndex);
      }

      // Create the container
      const newContainer = createContainerAPI(
        newContainerId,
        name,
        newOrder,
        rowIndex,
        {
          configuration: {
            width: calculatedWidth,
            height: 'auto',
            alignment: 'left',
            layout: 'vertical',
            showTitle: true,
          },
          metadata: {
            type: parentContainerId ? 'nested-container' : 'container',
            isNested: !!parentContainerId,
            parentContainerId,
            rowIndex,
            nestingLevel: parentContainerId ? 1 : 0,
          },
        }
      );

      // Create width update commands for existing containers
      const widthUpdates = createWidthUpdates(rowContainers, widthMap);
      const widthUpdateCommands = widthUpdates
        .filter((wu) => wu.containerId !== newContainerId)
        .map((wu) =>
          createUpdateContainerCommand(
            {
              containerId: wu.containerId,
              canvasId: resolvedCanvasId,
              updates: {
                configuration: { width: wu.newWidth },
              },
              // Note: previousValues are populated by the command executor during execution
            },
            `Update width: ${wu.previousWidth} → ${wu.newWidth}`
          )
        );

      // Create add container command
      const addCommand = createAddContainerCommand(
        {
          container: newContainer,
          canvasId: resolvedCanvasId,
          parentContainerId,
        },
        `Add container: ${name}`
      );

      // Combine all update commands (order shifts + width updates)
      const allUpdateCommands = [...orderShiftCommands, ...widthUpdateCommands];

      // Execute as batch if there are any update commands
      if (allUpdateCommands.length > 0) {
        const batchCommand = createBatchCommand(
          [...allUpdateCommands, addCommand],
          `Add container "${name}" with order shifting and width redistribution`
        );
        dispatch({ type: 'EXECUTE_COMMAND', payload: batchCommand });
      } else {
        dispatch({ type: 'EXECUTE_COMMAND', payload: addCommand });
      }

      return {
        success: true,
        container: newContainer,
        rowIndex,
        widthUpdates,
        validation,
      };
    },
    [containers, resolvedCanvasId, state, dispatch]
  );

  /**
   * Adds a container after a row (creates a new row)
   * Shifts existing containers down to make room for the new row
   */
  const addContainerAfterRow = useCallback(
    (afterRowIndex: number, name: string, options: AddContainerOptions = {}): AddContainerResult => {
      if (!resolvedCanvasId) {
        return {
          success: false,
          validation: validationFailure([
            createValidationError('CANVAS_NOT_FOUND', 'No active canvas found'),
          ]),
        };
      }

      const { parentContainerId } = options;
      const newRowIndex = afterRowIndex + 1;

      // Validate nesting if nested
      if (parentContainerId) {
        const nestingValidation = validateNestingDepth(containers, parentContainerId);
        if (!nestingValidation.isValid) {
          return { success: false, validation: nestingValidation };
        }
      }

      // Find containers that need their rowIndex shifted (rowIndex > afterRowIndex)
      // Filter out nested containers as they have their own row indexing within parent
      const containersToShift = containers.filter(
        (c) => (c.rowIndex ?? 0) > afterRowIndex && !c.metadata?.isNested
      );

      // Create UPDATE commands to shift existing containers down by 1
      const shiftCommands = containersToShift.map((container) =>
        createUpdateContainerCommand(
          {
            containerId: container.id,
            canvasId: resolvedCanvasId,
            updates: {
              rowIndex: (container.rowIndex ?? 0) + 1,
              metadata: container.metadata
                ? { ...container.metadata, rowIndex: (container.rowIndex ?? 0) + 1 }
                : undefined,
            },
          },
          `Shift row: ${container.rowIndex} → ${(container.rowIndex ?? 0) + 1}`
        )
      );

      // Create the container with 100% width (new row always gets full width)
      const newContainerId = generateId('container');
      const newContainer = createContainerAPI(
        newContainerId,
        name,
        0, // First in new row
        newRowIndex,
        {
          configuration: {
            width: '100%',
            height: 'auto',
            alignment: 'left',
            layout: 'vertical',
            showTitle: true,
          },
          metadata: {
            type: parentContainerId ? 'nested-container' : 'container',
            isNested: !!parentContainerId,
            parentContainerId,
            rowIndex: newRowIndex,
            nestingLevel: parentContainerId ? 1 : 0,
          },
        }
      );

      // Create add container command
      const addCommand = createAddContainerCommand(
        {
          container: newContainer,
          canvasId: resolvedCanvasId,
          parentContainerId,
        },
        `Add container: ${name} (new row)`
      );

      // Execute as batch if there are containers to shift, otherwise just add
      if (shiftCommands.length > 0) {
        const batchCommand = createBatchCommand(
          [...shiftCommands, addCommand],
          `Add container "${name}" with row shifting`
        );
        dispatch({ type: 'EXECUTE_COMMAND', payload: batchCommand });
      } else {
        dispatch({ type: 'EXECUTE_COMMAND', payload: addCommand });
      }

      return {
        success: true,
        container: newContainer,
        rowIndex: newRowIndex,
        validation: validationSuccess(),
      };
    },
    [containers, resolvedCanvasId, state, dispatch]
  );

  /**
   * Adds a nested container within a parent container
   * Supports insertAtOrder for inserting at specific position with order shifting
   */
  const addNestedContainer = useCallback(
    (parentId: string, name: string, options: AddContainerOptions = {}): AddContainerResult => {
      if (!resolvedCanvasId) {
        return {
          success: false,
          validation: validationFailure([
            createValidationError('CANVAS_NOT_FOUND', 'No active canvas found'),
          ]),
        };
      }

      const { rowIndex = 0, widthStrategy = 'equal', insertAtOrder } = options;

      // Validate nesting depth
      const nestingValidation = validateNestingDepth(containers, parentId);
      if (!nestingValidation.isValid) {
        return { success: false, validation: nestingValidation };
      }

      // Get existing nested containers
      const parentContainer = baseSelectors.selectContainerById(state, parentId).element;
      if (!parentContainer) {
        return {
          success: false,
          validation: validationFailure([
            createValidationError('CONTAINER_NOT_FOUND', `Parent container "${parentId}" not found`),
          ]),
        };
      }

      const nestedContainers = parentContainer.content?.nestedContainers || [];
      const rowContainers = nestedContainers.filter(
        (c) => (c.rowIndex ?? 0) === rowIndex
      );

      // Validate row constraint for nested containers
      const rowValidation = validateRowConstraint(rowContainers, rowIndex);
      if (!rowValidation.isValid) {
        return { success: false, validation: rowValidation };
      }

      // Calculate width
      const newContainerId = generateId('nested-container');
      const widthMap = calculateWidthsOnAdd(rowContainers, newContainerId, widthStrategy);
      const calculatedWidth = widthMap.get(newContainerId) || distributeWidthsEqually(rowContainers.length + 1);

      // Determine the order for the new nested container
      let newOrder: number;
      const orderShiftCommands: ReturnType<typeof createUpdateContainerCommand>[] = [];

      if (insertAtOrder !== undefined) {
        // Clamp insertAtOrder to valid range [0, rowContainers.length]
        newOrder = Math.max(0, Math.min(insertAtOrder, rowContainers.length));

        // Find nested containers at or after the insert position and shift their order +1
        const containersToShift = rowContainers
          .filter((c) => c.order >= newOrder)
          .sort((a, b) => b.order - a.order); // Sort descending to shift from end first

        for (const container of containersToShift) {
          orderShiftCommands.push(
            createUpdateContainerCommand(
              {
                containerId: container.id,
                canvasId: resolvedCanvasId,
                updates: {
                  order: container.order + 1,
                },
              },
              `Shift nested order: ${container.order} → ${container.order + 1}`
            )
          );
        }
      } else {
        // Use existing behavior: append at end
        newOrder = rowContainers.length;
      }

      // Create the nested container with dynamic nesting level
      const parentNestingLevel = getNestingDepthOfParent(parentId);
      const newContainer = createContainerAPI(
        newContainerId,
        name,
        newOrder,
        rowIndex,
        {
          configuration: {
            width: calculatedWidth,
            height: 'auto',
            alignment: 'left',
            layout: 'vertical',
            showTitle: true,
          },
          metadata: {
            type: 'nested-container',
            isNested: true,
            parentContainerId: parentId,
            rowIndex,
            nestingLevel: parentNestingLevel + 1,
          },
        }
      );

      // Create width update commands for existing nested containers
      const widthUpdates = createWidthUpdates(rowContainers, widthMap);
      const widthUpdateCommands = widthUpdates
        .filter((wu) => wu.containerId !== newContainerId)
        .map((wu) =>
          createUpdateContainerCommand(
            {
              containerId: wu.containerId,
              canvasId: resolvedCanvasId,
              updates: {
                configuration: { width: wu.newWidth },
              },
            },
            `Update nested width: ${wu.previousWidth} → ${wu.newWidth}`
          )
        );

      // Create add container command
      const addCommand = createAddContainerCommand(
        {
          container: newContainer,
          canvasId: resolvedCanvasId,
          parentContainerId: parentId,
        },
        `Add nested container: ${name}`
      );

      // Combine all update commands (order shifts + width updates)
      const allUpdateCommands = [...orderShiftCommands, ...widthUpdateCommands];

      // Execute as batch if there are any update commands
      if (allUpdateCommands.length > 0) {
        const batchCommand = createBatchCommand(
          [...allUpdateCommands, addCommand],
          `Add nested container "${name}" with order shifting and width redistribution`
        );
        dispatch({ type: 'EXECUTE_COMMAND', payload: batchCommand });
      } else {
        dispatch({ type: 'EXECUTE_COMMAND', payload: addCommand });
      }

      return {
        success: true,
        container: newContainer,
        rowIndex,
        widthUpdates,
        validation: validationSuccess(),
      };
    },
    [containers, resolvedCanvasId, state, dispatch]
  );

  /**
   * Adds a nested container after a row within parent (creates a new nested row)
   * Shifts existing nested containers down to make room for the new row
   */
  const addNestedContainerAfterRow = useCallback(
    (parentId: string, afterRowIndex: number, name: string, options: AddContainerOptions = {}): AddContainerResult => {
      if (!resolvedCanvasId) {
        return {
          success: false,
          validation: validationFailure([
            createValidationError('CANVAS_NOT_FOUND', 'No active canvas found'),
          ]),
        };
      }

      // Validate nesting depth
      const nestingValidation = validateNestingDepth(containers, parentId);
      if (!nestingValidation.isValid) {
        return { success: false, validation: nestingValidation };
      }

      // Get parent container
      const parentContainer = baseSelectors.selectContainerById(state, parentId).element;
      if (!parentContainer) {
        return {
          success: false,
          validation: validationFailure([
            createValidationError('CONTAINER_NOT_FOUND', `Parent container "${parentId}" not found`),
          ]),
        };
      }

      const nestedContainers = parentContainer.content?.nestedContainers || [];
      const newRowIndex = afterRowIndex + 1;

      // Find nested containers that need their rowIndex shifted (rowIndex > afterRowIndex)
      const containersToShift = nestedContainers.filter(
        (c) => (c.rowIndex ?? 0) > afterRowIndex
      );

      // Create UPDATE commands to shift existing nested containers down by 1
      const shiftCommands = containersToShift.map((container) =>
        createUpdateContainerCommand(
          {
            containerId: container.id,
            canvasId: resolvedCanvasId,
            updates: {
              rowIndex: (container.rowIndex ?? 0) + 1,
              metadata: container.metadata
                ? { ...container.metadata, rowIndex: (container.rowIndex ?? 0) + 1 }
                : undefined,
            },
          },
          `Shift nested row: ${container.rowIndex} → ${(container.rowIndex ?? 0) + 1}`
        )
      );

      // Create the nested container with 100% width (new row always gets full width)
      const newContainerId = generateId('nested-container');
      const parentNestingLevel = getNestingDepthOfParent(parentId);
      const newContainer = createContainerAPI(
        newContainerId,
        name,
        0, // First in new row
        newRowIndex,
        {
          configuration: {
            width: '100%',
            height: 'auto',
            alignment: 'left',
            layout: 'vertical',
            showTitle: true,
          },
          metadata: {
            type: 'nested-container',
            isNested: true,
            parentContainerId: parentId,
            rowIndex: newRowIndex,
            nestingLevel: parentNestingLevel + 1,
          },
        }
      );

      // Create add container command
      const addCommand = createAddContainerCommand(
        {
          container: newContainer,
          canvasId: resolvedCanvasId,
          parentContainerId: parentId,
        },
        `Add nested container: ${name} (new row)`
      );

      // Execute as batch if there are containers to shift, otherwise just add
      if (shiftCommands.length > 0) {
        const batchCommand = createBatchCommand(
          [...shiftCommands, addCommand],
          `Add nested container "${name}" with row shifting`
        );
        dispatch({ type: 'EXECUTE_COMMAND', payload: batchCommand });
      } else {
        dispatch({ type: 'EXECUTE_COMMAND', payload: addCommand });
      }

      return {
        success: true,
        container: newContainer,
        rowIndex: newRowIndex,
        validation: validationSuccess(),
      };
    },
    [containers, resolvedCanvasId, state, dispatch]
  );

  return {
    // Data
    containerRows,
    // Queries
    canAddToRowIndex,
    getNextAvailableSlot: getNextSlot,
    getNestedContainerRows,
    canNestIn,
    getNextContainerNumber,
    canAddToNestedRow,
    getNextNestedContainerNumber,
    getNestedContainerName,
    // Operations
    addContainerToRow,
    addContainerAfterRow,
    addNestedContainer,
    addNestedContainerAfterRow,
    // Constants
    MAX_CONTAINERS_PER_ROW: CONTAINER_CONSTRAINTS.MAX_CONTAINERS_PER_ROW,
    MAX_NESTING_DEPTH: CONTAINER_CONSTRAINTS.MAX_NESTING_DEPTH,
  };
};

export default useContainerLayout;
